import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icone } from '../ui/icones';
import { travarFundo, useAberturaAnimada } from '../ui/Modal';
import { empilharEsc } from '../ui/pilha-do-esc';

/**
 * O diálogo da busca e das notificações do topo (o `Modal` do YardHub em
 * `ws-search-modal`/`ws-notifications-modal`): sobreposição, cartão com o X
 * no canto, olho de seção, título e frase. Não é o `Modal` de src/ui porque
 * aquele é um formulário com "Cancelar" e "Salvar" no rodapé, e aqui não há
 * nada para salvar: a busca navega e as notificações se abrem.
 *
 * Usa as mesmas peças do `Modal`: a pilha do Esc (o Esc fecha só a camada de
 * cima), a trava de rolagem do fundo, a abertura animada e o foco preso
 * dentro enquanto está aberto, devolvido a quem abriu ao fechar.
 */
export function DialogoDoTopo({
  aberto,
  aoFechar,
  olho,
  titulo,
  frase,
  className = '',
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  olho: string;
  titulo: string;
  frase?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const { montado, ligado } = useAberturaAnimada(aberto);
  const caixa = useRef<HTMLDivElement>(null);
  const tituloId = useId();
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;

  useEffect(() => {
    if (!aberto) return;
    const quemAbriu = document.activeElement as HTMLElement | null;
    const desempilhar = empilharEsc(() => fechar.current());
    const soltar = travarFundo();
    function prender(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focaveis = caixa.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (!focaveis || focaveis.length === 0) return;
      const primeiro = focaveis[0]!;
      const ultimo = focaveis[focaveis.length - 1]!;
      if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
    }
    document.addEventListener('keydown', prender);
    // O primeiro campo (a busca), senão o primeiro botão depois do X.
    const relogio = window.setTimeout(() => {
      const alvo = caixa.current?.querySelector<HTMLElement>('input')
        ?? caixa.current?.querySelector<HTMLElement>('.dialogo-topo__corpo button');
      alvo?.focus();
    }, 0);
    return () => {
      desempilhar();
      soltar();
      document.removeEventListener('keydown', prender);
      window.clearTimeout(relogio);
      quemAbriu?.focus?.();
    };
  }, [aberto]);

  if (!montado) return null;
  return (
    <div className={`overlay${ligado ? ' on' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div ref={caixa} className={`modal dialogo-topo ${className}`} role="dialog" aria-modal="true" aria-labelledby={tituloId}>
        <button type="button" className="fechar-x" onClick={aoFechar}>
          <Icone.Fechar titulo="Fechar" tamanho={18} />
        </button>
        <span className="dialogo-topo__olho">{olho}</span>
        <h3 id={tituloId}>{titulo}</h3>
        {frase && <p className="desc-modal">{frase}</p>}
        <div className="dialogo-topo__corpo">{children}</div>
      </div>
    </div>
  );
}
